import {Button, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {ClientFunction, FunctionArg} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {FormulaInputTokenType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, IReactionDisposer, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as React from 'react';
import {CSSProperties} from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/InputDocView.less');
const cx = classnames.bind(styles);

interface IInputDocViewProps
{
    ctx?:IWSPMeshContext;
    formulaInputHeight:number;
}

@inject('ctx')
@observer
export class InputDocView extends React.Component<IInputDocViewProps, any>
{
    private readonly _id:GUID = IdUtil.newGUID();

    private _updateClientFunctionReaction:IReactionDisposer = null;

    @observable
    private _popoverSource:HTMLElement = null;

    @computed
    private get _isOpened():boolean
    {
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;

        const {
            isDocOpen,
            docFunction,
            isSelectCellRefMode,
            tokenAtCursor,
            textSelection,
            formulaTokens,
            isInputJustMounted,
        } = inputState;

        const isCellRefToken:boolean = tokenAtCursor
            && (tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange);

        // const cursorAtLastPosition = formulaTokens && formulaTokens.length
        //     && formulaTokens[formulaTokens.length -1].token.endIndex === textSelection.startIndex;

        return isDocOpen && !!docFunction && (!isSelectCellRefMode || !isCellRefToken) && !isInputJustMounted;
    }

    @computed
    private get _isDocVisible():boolean
    {
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;

        const {isDocVisible} = inputState;

        return isDocVisible;
    }

    @computed
    private get _docFunction():ClientFunction
    {
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;

        const {docFunction} = inputState;

        return docFunction;
    }

    @computed
    private get _funcExample():string
    {
        return this._docFunction.example;
    }

    @computed
    private get _funcTip():string
    {
        return this._docFunction.tip;
    }

    @computed
    private get _funcName():string
    {
        return this._docFunction.getQualifiedName();

    }

    @computed
    private get _funcNamespace():string
    {
        return this._docFunction && this._docFunction.namespace;
    }

    @computed
    private get _funcIsCloud():boolean
    {
        return this._funcNamespace === 'cloud';

    }

    @computed
    private get _functionArguments():Array<FunctionArg>
    {
        return this._docFunction && this._docFunction.args.filter(value => value.type.type !== SheetType.SheetAPI);
    }

    @computed
    private get _functionArgumentIndex():number
    {
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;

        const {currentFunctionArgumentIndex} = inputState;

        return currentFunctionArgumentIndex;
    }

    @computed
    private get _isDocCollapsed():boolean
    {
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;

        const {isDocCollapsed} = inputState;

        return isDocCollapsed;
    }

    @bind
    private _onCollapseDocClick():void
    {
        const {ctx} = this.props;
        controllers.ui.spreadsheets.meshInput.toggleDocCollapse();
    }

    @bind
    private _onShowDocClick():void
    {
        const {ctx} = this.props;
        controllers.ui.spreadsheets.meshInput.setDocVisibility(true);
    }

    @bind
    private _onCloseDocClick():void
    {
        const {ctx} = this.props;
        controllers.ui.spreadsheets.meshInput.setDocVisibility(false);
    }

    private static _renderDocItem(title:string, text:string, isExample:boolean = false):JSX.Element
    {
        return <div>
            <div className={styles.docItemTitle}>{title}</div>
            <div className={cx('docItemText', {isExample})}>
                {text && text.length > 0 ? text : '???'}
            </div>
        </div>;
    }

    private _renderArguments():Array<JSX.Element>
    {
        return this._functionArguments && this._functionArguments.map(
            (arg:FunctionArg, index:number) =>
            {
                if( arg.optional )
                {
                    if( arg.repeatable )
                    {
                        return <span
                            key={index}
                            className={cx('argument', {isSelected: index <= this._functionArgumentIndex})}
                        >
                            {(index > 0 ? ', [' : '[') + arg.name + ', ' + arg.name + ', ...]'}
                        </span>;
                    }
                    else
                    {
                        return <span
                            key={index}
                            className={cx('argument', {isSelected: index === this._functionArgumentIndex})}
                        >
                            {(index > 0 ? ', [' : '[') + arg.name + ']'}
                        </span>;
                    }
                }
                else
                {
                    if( arg.repeatable )
                    {
                        return <span
                            key={index}
                            className={cx('argument', {isSelected: index <= this._functionArgumentIndex})}
                        >
                            {(index > 0 ? ', ' : '') + arg.name + ', ' + arg.name + ', ...'}
                        </span>;
                    }
                    else
                    {
                        return <span
                            key={index}
                            className={cx('argument', {isSelected: index === this._functionArgumentIndex})}
                        >
                            {(index > 0 ? ', ' : '') + arg.name}
                        </span>;
                    }
                }
            }
        );
    }

    private _renderArgumentsInfo():JSX.Element
    {
        if( !this._functionArguments || this._functionArguments.length === 0 ) return null;

        return (
            <div>
                <div className={cx('line')} />

                {this._functionArguments.map(
                    (arg:FunctionArg, index:number) =>
                    {
                        if( arg.optional )
                        {
                            if( arg.repeatable )
                            {
                                return <div key={index}>
                                    <div className={styles.docItemTitleArg}>
                                        <span
                                            className={cx('docItemTitleText', {isSelected: index <= this._functionArgumentIndex})}
                                        >
                                            {arg.name}
                                        </span>
                                        <span className={styles.itemPackagePill}>{' - [optional] repeatable'}</span>
                                    </div>
                                    {
                                        arg.tip && arg.tip.length > 0 &&
                                        <div className={styles.docItemText}>{arg.tip}</div>
                                    }
                                </div>;
                            }
                            else
                            {
                                return <div key={index}>
                                    <div className={styles.docItemTitleArg}>
                                        <span
                                            className={cx('docItemTitleText', {isSelected: index === this._functionArgumentIndex})}>
                                            {arg.name}
                                        </span>
                                        <span className={styles.itemPackagePill}>{' - [optional]'}</span>
                                    </div>
                                    {
                                        arg.tip && arg.tip.length > 0 &&
                                        <div className={styles.docItemText}>{arg.tip}</div>
                                    }
                                </div>;
                            }
                        }
                        else
                        {
                            if( arg.repeatable )
                            {
                                return <div key={index}>
                                    <div className={styles.docItemTitleArg}>
                                        <span
                                            className={cx('docItemTitleText', {isSelected: index <= this._functionArgumentIndex})}
                                        >
                                            {arg.name}
                                        </span>
                                        <span className={styles.itemPackagePill}>{' - repeatable'}</span>
                                    </div>
                                    {
                                        arg.tip && arg.tip.length > 0 &&
                                        <div className={styles.docItemText}>{arg.tip}</div>
                                    }
                                </div>;
                            }
                            else
                            {
                                return <div key={index}>
                                    <div className={styles.docItemTitleArg}>
                                        <span
                                            className={cx('docItemTitleText', {isSelected: index === this._functionArgumentIndex})}
                                        >
                                            {arg.name}
                                        </span>
                                    </div>
                                    {
                                        arg.tip && arg.tip.length > 0 &&
                                        <div className={styles.docItemText}>{arg.tip}</div>
                                    }
                                </div>;
                            }
                        }
                    }
                )}
            </div>
        );
    }

    public componentDidMount():void
    {
        const {ctx} = this.props;

        this._updateClientFunctionReaction = controllers.ui.spreadsheets.meshInput.initCurrentFunctionReaction();
    }

    public componentWillUnmount():void
    {
        if( this._updateClientFunctionReaction )
        {
            this._updateClientFunctionReaction();
            this._updateClientFunctionReaction = null;
        }
    }

    @action.bound
    private _onHandlePopoverSourceRef(elementRef:HTMLElement)
    {
        const {sheetId} = this.props.ctx;

        DomUtils.eachParentElement(elementRef, parentElement =>
        {
            if( !parentElement )
                return false;

            if( parentElement.id === sheetId )
            {
                this._popoverSource = parentElement;
                return false;
            }
        });
    }

    public render():JSX.Element
    {
        return (
            <div className={styles.base} ref={this._onHandlePopoverSourceRef}>
                {
                    this._isOpened && (
                        this._isDocVisible
                            ? this._renderPopover()
                            : this._renderBtn()
                    )
                }
            </div>
        );
    }

    private _renderBtn()
    {
        const {formulaInputHeight} = this.props;
        const btnStyle:CSSProperties = {};

        //       ---------------------------
        //       | formulaInputHeight
        //    ---|
        // 20 |? |
        //    ---|
        //       |  min-height = 33px
        //       ---------------------------
        btnStyle.marginBottom = formulaInputHeight > 33
            ? (formulaInputHeight - 20) / 2
            : 6;

        return (
            <div
                className={styles.docShowButton}
                style={btnStyle}
                onClick={this._onShowDocClick}
            >
                ?
            </div>
        );
    }

    @bind
    private _onClickFunctionDoc(e:React.MouseEvent<HTMLElement>, functionName:string):void
    {
        e.stopPropagation();
        e.preventDefault();

        const {lx} = controllers.ui;

        lx.showLxFrame();

        lx.postMessage({
            source: 'designer',
            event_type: 'show_function_docs',
            has_function_name: !!functionName,
            function_name: functionName ? functionName.toLowerCase() : null,
            is_cloud_flow: this._funcIsCloud,
        });
    }

    private _renderPopover()
    {
        if( !this._popoverSource ) return null;

        const about = this._funcIsCloud ? 'cloud functions' : this._funcName;

        return (
            <Popover
                id={this._id}
                popupSource={this._popoverSource}
                sourceAnchor={SourceAnchor.LeftBottom}
                popupAnchor={PopupAnchor.LeftTop}
                ignoreEsc={true}
                isModal={false}
                hasModalBackground={true}
                className={cx('popover')}
            >
                <div className={styles.popoverContent}>
                    <div className={styles.itemRoot}>

                        <div className={styles.itemValueWrapper}>
                            <div className={styles.itemValue}>
                                <span className={cx('functionName')}>
                                    {this._funcName}
                                </span>
                                <span className={cx('functionArguments')}>
                                    ({this._renderArguments()})
                                </span>
                            </div>
                            <div
                                className={cx('iconDoc')}
                                onClick={(e:React.MouseEvent<HTMLElement>) => this._onClickFunctionDoc(e, this._funcName)}
                            >
                                <WSPIcon
                                    name={WSPIcons.visualDesigner.spreadsheet.Help}
                                    size={WSPIconSize.medium20}
                                />
                            </div>
                        </div>

                        <Button
                            style={ButtonStyle.SimpleSecondary}
                            icon={MDIcons.common.ChevronRight}
                            iconSize={MDIconSize.medium16}
                            className={cx('docButton', 'expand', {isCollapsed: this._isDocCollapsed})}
                            onClick={this._onCollapseDocClick}
                        />
                        <Button
                            style={ButtonStyle.SimpleSecondary}
                            icon={MDIcons.common.Close}
                            iconSize={MDIconSize.medium16}
                            className={styles.docButton}
                            onClick={this._onCloseDocClick}
                        />
                    </div>
                    {
                        !this._isDocCollapsed &&
                        <div className={styles.docContent}>
                            {InputDocView._renderDocItem('Example', this._funcExample, true)}
                            {InputDocView._renderDocItem('Summary', this._funcTip)}
                            {this._renderArgumentsInfo()}
                        </div>
                    }
                </div>

                <div className={cx('popoverFooter')}>
                    <div
                        className={cx('footerLink')}
                        onClick={(e:React.MouseEvent<HTMLElement>) =>
                        {
                            this._onClickFunctionDoc(e, this._funcName);
                        }}
                    >
                        Learn more about {about}
                    </div>
                </div>

            </Popover>
        );
    }
}
